import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '../services/supabase';
import { cuilToEmail } from '../utils/identity';
import type { Ficha } from '../types';

interface AuthState {
  /** true hasta saber si hay sesion y, si la hay, cargar la ficha del afiliado. */
  loading: boolean;
  session: Session | null;
  ficha: Ficha | null;
  /** La cuenta inicio sesion pero no es de un afiliado activo (se la desconecta). */
  accesoDenegado: boolean;
  signIn: (cuil: string, password: string) => Promise<string | null>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

const LOGIN_AUDIT_KEY = 'atsa.afiliados.login_audit_uid';

/** Registra el LOGIN una sola vez por sesion de pestaña. Best-effort: nunca rompe el ingreso. */
async function auditarLogin(userId: string) {
  try {
    if (sessionStorage.getItem(LOGIN_AUDIT_KEY) === userId) return;
    sessionStorage.setItem(LOGIN_AUDIT_KEY, userId);
  } catch {
    // sessionStorage no disponible: se registra igual
  }
  try {
    await supabase.rpc('log_audit_event', { p_accion: 'LOGIN', p_datos: { sitio: 'afiliados' } });
  } catch {
    // el audit es valioso pero no critico
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [sessionLoading, setSessionLoading] = useState(true);
  const [ficha, setFicha] = useState<Ficha | null>(null);
  const [fichaLoading, setFichaLoading] = useState(false);
  const [accesoDenegado, setAccesoDenegado] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setSessionLoading(false);
    });
    // Solo guardar la sesion aca: llamar a supabase dentro de este callback puede trabar el cliente.
    const { data } = supabase.auth.onAuthStateChange((_event, s) => setSession(s));
    return () => data.subscription.unsubscribe();
  }, []);

  const userId = session?.user.id ?? null;
  useEffect(() => {
    if (!userId) {
      setFicha(null);
      setFichaLoading(false);
      return;
    }
    let cancelled = false;
    setFichaLoading(true);
    supabase
      .from('vw_mi_ficha')
      .select('*')
      .maybeSingle()
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error || !data) {
          // Sesion valida pero sin ficha: no es un afiliado activo (o no es afiliado)
          setFicha(null);
          setAccesoDenegado(true);
          void supabase.auth.signOut();
        } else {
          setFicha(data as Ficha);
          setAccesoDenegado(false);
          void auditarLogin(userId);
        }
        setFichaLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const value = useMemo<AuthState>(
    () => ({
      loading: sessionLoading || fichaLoading,
      session,
      ficha,
      accesoDenegado,
      signIn: async (cuil, password) => {
        setAccesoDenegado(false);
        const { error } = await supabase.auth.signInWithPassword({ email: cuilToEmail(cuil), password });
        if (!error) return null;
        return /invalid login credentials/i.test(error.message)
          ? 'El CUIL o la contraseña no son correctos.'
          : 'No pudimos iniciar sesión. Probá de nuevo en unos minutos.';
      },
      signOut: async () => {
        try {
          sessionStorage.removeItem(LOGIN_AUDIT_KEY);
        } catch {
          // ignorar
        }
        await supabase.auth.signOut();
      },
    }),
    [sessionLoading, fichaLoading, session, ficha, accesoDenegado],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth debe usarse dentro de AuthProvider');
  return ctx;
}
